import { useQuery } from '@tanstack/react-query';

import { apiFetchJson } from '../../lib/apiFetch.js';
import { MIN_QUERY_LENGTH, type SearchResponse } from './contractsMirror.js';

/**
 * One request per pause in typing, for `GET /api/v1/search`.
 *
 * NO `type` PARAMETER IS SENT, and that is the point. The endpoint answers every
 * type this session may reach in ONE round trip (D1 refuses a compound SELECT
 * past ten terms, so the service batches nine statements instead), and it sends
 * the per-type counts with them. Asking again per tab would spend nine more
 * queries to learn what the first answer already contained, and the tab row
 * would have nothing to count until each tab had been visited. So the palette
 * filters by type in the browser and the tabs are instant.
 *
 * `placeholderData` keeps the previous answer on screen while the next one is in
 * flight: without it every keystroke empties the list and the panel flickers
 * between results and "no matches".
 */
export function searchQueryKey(query: string): readonly unknown[] {
  return ['search', query] as const;
}

export function useSearchQuery(query: string, enabled = true) {
  const trimmed = query.trim();
  return useQuery<SearchResponse>({
    queryKey: searchQueryKey(trimmed),
    enabled: enabled && trimmed.length >= MIN_QUERY_LENGTH,
    placeholderData: (previous) => previous,
    // Records change while someone is looking at them, and the palette is a
    // navigation surface rather than a report, so a short window is enough to
    // make backspacing feel instant without ever serving a stale record list.
    staleTime: 30_000,
    queryFn: () => apiFetchJson<SearchResponse>(`/api/v1/search?q=${encodeURIComponent(trimmed)}`),
  });
}
